import React, {useCallback, useState} from "react";
import {observer} from "mobx-react-lite";
import Modal from "@mui/joy/Modal";
import intl from "react-intl-universal";
import {useAppState, useRootStore, useDsManager} from "@stores/AppStateContext";

import {RelatedPageParams, MODALS} from "../modal-names";

import {RocketLaunch} from "../../../components/icons/RocketLaunchIcon";
import {AiIcon} from "../../../components/icons/AiIcon"
import {openQuizPage} from "../../Quizzes/common/openQuizPage"
import {RelatedItemsSelect} from "../../../elements/context-menu/RelatedItemContextMenu/RelatedItemsSelect"
import {Button} from "../../../components/Button/Button";
import {RestrictedNumberPicker} from "../../../components/RestrictedNumberPicker/RestrictedNumberPicker";
import {UpgradeNoticeWithCounter} from "../../../components/UpgradeNoticeWithCounter/UpgradeNoticeWithCounter";
import {isFreemium} from "../../../../stores/client";
import {PercentInputField} from "../../../components/PercentInputField/PercentInputField";
import {generateQuizWithAi} from "../../../../actions/quiz/generateQuizWithAi";
import {CloseIcon} from "../../../components/icons/CloseIcon";
import {logErrorToSentry} from "../../../../utils/errors/logErrorToSentry";

import {ActionName, isAllowed, FREEMIUM_LIMITS} from "../../../../access_rights/isAllowed"
import {PAGES} from "../../../../stores/ViewStore";
import {snackBarRef} from "../../../../stores/snackbarRef";
import {screenSizeProvider} from "../../../../stores/ScreenSizeProvider";

import "./AiQuiz.css"

export const AiQuiz = observer((
    {docId}: RelatedPageParams
) => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const dsManager = useDsManager();
    const isMobile = screenSizeProvider.view !== 'desktop';
    const [selectedItemId, setSelectedItemId] = useState<string | null>(docId ? docId : null);
    const [selectedNumber, setSelectedNumber] = useState<number>(10);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const [passPercentage, setPassPercentage] = useState<number | ''>('');
    const clientDb = dsManager!.clientAR.row!;
    const currentAiUsage = clientDb.meta.aiUsageCount || 0;

    const AiQuizOptions = [
        {value: 5, onPremium: false},
        {value: 10, onPremium: false},
        {value: 15, onPremium: true},
        {value: 20, onPremium: true},
    ];

    const handleClose = useCallback(() => {
        appState.setModal(null);
        appState.setContextMenu(null);
    }, []);

    const onError = () => {
        snackBarRef.current?.enqueueSnackbar({
            message: intl.get('user-role.general_error').d(`Something went wrong, but we're actively working to fix this issue. In the meantime, please feel free to contact us.`),
            variant: 'error',
        })
    }
    const generateQuiz = async () => {
        if (isProcessing) {
            return
        }
        if (!selectedItemId) {
            setHasError(true);
            return
        }
        try {
            setHasError(false);
            setIsProcessing(true);
            snackBarRef.current?.enqueueSnackbar({
                message: intl.get('AiQuiz.snackBar.start.generate').d("AI is working to build your test...  It may take a few moments."),
                variant: 'ai',
            });
            handleClose()
            const response = await generateQuizWithAi(appState.userState!.wsServerConnection, {
                docId: selectedItemId,
                docTitle: rootStore.view.workspace?.items.get(selectedItemId)?.name,
                count: selectedNumber,
                requiredPassPercentage: passPercentage ? passPercentage : 100,
            });
            if (response.s === 200) {
                snackBarRef.current?.enqueueSnackbar({
                    message: intl.get('AiQuiz.success').d(`Test generated`),
                    actionDef: {
                        text: intl.get('AiQuiz.snackBar.success').d('VIEW'),
                        onClick: () => openQuizPage(rootStore, {
                            page: PAGES.QUIZ_VIEW,
                            quizId: response.quizId,
                        })
                    },
                    variant: 'success',
                });
            } else {
                onError();
            }
        } catch (error) {
            console.error(error);
            logErrorToSentry(error as Error);
            onError();
        }
        setIsProcessing(false);
    }

    const AiQuizUpgradeEnqueueSnackbar = () => snackBarRef.current!.enqueueSnackbar({
        isCloseIcon: false,
        variant: "warning",
        message: currentAiUsage === 3
            ? intl.getHTML('alert.subscription_required.createAiQuiz').d(`You've hit the limit of <b>3 AI Test Generations.</b> Upgrade your plan to generate more tests.`)
            : intl.getHTML('alert.subscription_required.createQuiz').d(`You've hit the limit of <b>3 tests.</b> Upgrade your plan to create more tests.`),
        Icon: RocketLaunch,
        action: () => (
            <Button
                text={intl.get('upgrade.button').d('Upgrade plan')}
                type={'neutral'}
                style={'soft'}
                onClick={() => {
                    appState.setModal({name: MODALS.CHECKOUT, params: undefined});
                }}
            />
        ),
        autoHideDuration: 10000,
    })

    const isAllowedToGenerate = isAllowed(dsManager!, {
        actionName: ActionName.AiGenerate,
        currentUsage: currentAiUsage,
    })

    return (
        <Modal
            onClose={handleClose}
            open={appState.topModal === MODALS.QUIZ_AI}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'right',
                zIndex: 4000,
            }}
        >
            <div className="AiQuiz">
                <div
                    className='AiModalBase_close'
                >
                    <CloseIcon
                        className="AiModalClose_button"
                        additionalPadding={8}
                        size={16}
                        onClick={handleClose}/>
                </div>
                <div className="AiQuiz__header title-large-bold">
                    <AiIcon size={24} className="AiQuiz__header__icon"/>
                    <span className='AiQuiz__header__text'>{intl.get('AiQuiz.header').d('Generate Test with AI')}</span>
                </div>
                <div className="AiQuiz__related_page__header title-medium">
                    {intl.get('AiQuiz.related_pages.title').d('Related Page:')}
                </div>
                <div className="AiQuiz__related_page__text body-medium">
                    {intl.get('AiQuiz.related_pages.text').d('Generate a test based on a selected Page from the knowledge base.')}
                </div>
                <RelatedItemsSelect
                    itemId={selectedItemId}
                    setItemId={setSelectedItemId}
                    hasError={hasError && !selectedItemId}
                    errorText={intl.get('AiQuiz.required-item').d('Please select a Page to generate test')}
                    placeholder={isMobile
                        ? intl.get('quiz.related-pages.placeholder.mobile').d('Type to select a Page')
                        : intl.get('quiz.related-pages.placeholder').d('Type to select a Page to associate with this test')}
                />
                <div className="AiQuiz__pass__header title-medium">
                    {intl.get('AiQuiz.pass.header').d('Pass Mark:')}
                </div>
                <div className="AiQuiz__pass__text body-medium">
                    {intl.get('AiQuiz.pass.text').d('Choose the minimum percentage of correct answers required to pass the test.')}
                </div>
                <div className="AiQuiz__pass-inputBlock">
                    <PercentInputField
                        value={passPercentage}
                        onChange={(newValue) => setPassPercentage(newValue)}
                    />
                </div>
                <div className="AiQuiz__NOQ__header title-medium">
                    {intl.get('AiQuiz.NOQ.header').d('Number of Questions:')}
                </div>
                <div className="AiQuiz__NOQ__text body-medium">
                    {intl.get('AiQuiz.NOQ.text').d('Select how many questions you want in the test.')}
                </div>
                <div className='AiQuiz__NOQ__numberBlock'>
                    <RestrictedNumberPicker
                        selectedNumber={selectedNumber}
                        setSelectedNumber={setSelectedNumber}
                        numbers={AiQuizOptions}
                    />
                </div>
                <Button
                    className="AiQuiz__button"
                    type={'ai'}
                    size={'md'}
                    Icon={AiIcon}
                    style={'solid'}
                    fullWidth={isMobile}
                    text={intl.get('AiQuiz.button').d('Generate with AI')}
                    onClick={isAllowedToGenerate ? generateQuiz : AiQuizUpgradeEnqueueSnackbar}
                    loading={isProcessing}
                />
                {
                    isFreemium(dsManager?.clientAR?.row?.subscription_plan) && (
                        <UpgradeNoticeWithCounter
                            type={'ai'}
                            className="AiQuiz__UpgradeLimitNotice"
                            Icon={AiIcon}
                            currentLimit={currentAiUsage}
                            totalLimit={FREEMIUM_LIMITS.AiQuizGenerationCount}
                            header={intl.get('AiQuiz.UpgradeLimit.header').d('AI Test Generation Limit:')}/>
                    )
                }
            </div>
        </Modal>
    )
})
